<template>
    <div>
        <b-modal v-model="visible"
                 :title="title"
                 size="xl"
                 no-close-on-backdrop
                 no-close-on-esc>
            <b-row class="mb-2">
                <b-col md="7" sm="12" class="my-1">
                    <b-form-group label-for="bulk-filter-input"
                                  label-align-sm="right"
                                  class="mb-0">
                        <b-input-group>
                            <b-form-input id="bulk-filter-input"
                                          v-model="filter"
                                          type="search"
                                          autocomplete="off"
                                          placeholder="Filtrera....">
                            </b-form-input>
                            <b-input-group-append>
                                <b-button :disabled="!filter" @click="filter = ''">
                                    Rensa
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col sm="12" md="5" class="my-1">
                    <b-form-group label="Per sida"
                                  label-for="bulk-per-page-select"
                                  label-cols-sm="6"
                                  label-align-sm="right"
                                  class="mb-0">
                        <PaginationPageSizeSelect id="bulk-per-page-select"
                                                  v-model="perPage"
                                                  view="bulkSendLackMailPositions">
                        </PaginationPageSizeSelect>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row v-show="totalRows > perPage">
                <b-col cols="12" class="my-1 text-center">
                    <b-pagination v-model="currentPage"
                                  :total-rows="totalRows"
                                  :per-page="perPage"
                                  align="center"
                                  class="my-0">
                    </b-pagination>
                </b-col>
            </b-row>

            <b-table :items="positions"
                     :fields="fields"
                     :sort-by.sync="sortBy"
                     :sort-desc.sync="sortDesc"
                     :sort-direction.sync="sortDirection"
                     :current-page="currentPage"
                     :per-page="perPage"
                     :filter="filter"
                     :filter-included-fields="['concatenatedNumbers', 'articleName', 'lackDescription', 'fixedStatusDescription']"
                     @filtered="onFiltered"
                     show-empty
                     small
                     :busy="isLoading"
                     no-border-collapse
                     sticky-header="63vh">                
                <template #cell(selected)="row">
                    <b-form-checkbox v-model="selectedIds"
                                     :value="row.item.id">
                    </b-form-checkbox>
                </template>

                <template #cell(status)="row">
                    <div class="overflow-hidden">
                        <span class="d-none d-xs-inline-block">Status: </span>
                        <ControlPositionStatusBall :status-id="row.item.statusId">
                        </ControlPositionStatusBall>
                    </div>
                </template>

                <template #cell(concatenatedNumbers)="row">
                    <div class="overflow-hidden">
                        {{ row.item.controlSerialNumber }}-{{ row.item.concatenatedNumbers }}
                    </div>
                </template>

                <template #cell(articleName)="row">
                    <div class="overflow-hidden">
                        <img v-if="row.item.fileSymbolId"
                             style="height: 1.5rem"
                             :src="getUrl(row.item.articleId)" />
                        {{ row.item.articleName }}
                    </div>
                </template>

                <template #cell(lackDescription)="row">
                    <div class="overflow-hidden">
                        {{ row.item.lackDescription }}
                    </div>
                </template>

                <template #cell(fixedStatusDescription)="row">
                    <div class="overflow-hidden">
                        {{ row.item.fixedStatusDescription }}
                    </div>
                </template>

                <template #cell(lackDueDate)="row">
                    <div class="overflow-hidden">
                        {{ row.item.lackDueDate | datetime('yyyy-MM-dd') }}
                    </div>
                </template>

                <template #cell(controlDoneDate)="row">
                    <div class="overflow-hidden">
                        {{ row.item.controlDoneDate | datetime('yyyy-MM-dd') }}
                    </div>
                </template>

                <template #empty>
                    <h4 class="text-center">Det finns inga brister</h4>
                </template>

                <template #emptyfiltered>
                    <div class="text-center">
                        <h4>Inga resultat</h4>
                        <h6>
                            <a href="#" @click.prevent="filter = ''">
                                Klicka här för att rensa aktiv filtrering
                            </a>
                        </h6>
                    </div>
                </template>
            </b-table>

            <b-row>
                <b-col v-show="totalRows > perPage"
                       cols="12"
                       class="my-1 text-center">
                    <b-pagination v-model="currentPage"
                                  :total-rows="totalRows"
                                  :per-page="perPage"
                                  align="center"
                                  class="my-0">
                    </b-pagination>
                </b-col>
            </b-row>

            <template slot="modal-footer">
                <b-button variant="warning"
                          class="mr-auto"
                          @click="closeModal">
                    Avbryt
                </b-button>

                <SendLackMailModal :title="'Skicka brist(er)'"
                                   :lackIds="selectedIds"
                                   @closed="onLackMailClosed">
                    <template v-slot:button="{ openSendModal }">
                        <b-button variant="primary"
                                  :disabled="selectedIds.length === 0"
                                  @click="openSendModal">
                            Skicka brist(er)
                        </b-button>
                    </template>
                </SendLackMailModal>
            </template>
        </b-modal>
    </div>
</template>

<script>
    import PaginationPageSizeSelect from '@/components/PaginationPageSizeSelect';
    import ControlPositionStatusBall from '@/components/control/ControlPositionStatusBall';
    import SendLackMailModal from '@/components/control/SendLackMailModal';
    import { getDownloadUrlByArticleId } from '@/helpers/file.helper';

    export default {
        name: 'SendMultipleLacksModal',
        components: {
            PaginationPageSizeSelect,
            ControlPositionStatusBall,
            SendLackMailModal
        },
        props: {
            positions: {
                type: Array,
                required: true
            },
            title: {
                type: String,
                default: 'Välj brist(er)'
            }
        },
        data() {
            return {
                visible: false,
                selectedIds: [],
                fields: [
                    {
                        key: 'selected',
                        label: '',
                        thStyle: { width: '50px' }
                    },
                    {
                        key: 'status',
                        label: '',
                        thStyle: { width: '50px' }
                    },
                    {
                        key: 'concatenatedNumbers',
                        label: '#',
                        sortable: true,
                        sortDirection: 'desc',
                        thStyle: { width: '100px' }
                    },
                    {
                        key: 'articleName',
                        label: 'Artikel',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'lackDescription',
                        label: 'Brist',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'fixedStatusDescription',
                        label: 'Åtgärdstatus',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'lackDueDate',
                        label: 'Åtgärdat senast',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'controlDoneDate',
                        label: 'Rapporterad',
                        sortable: true,
                        sortDirection: 'desc',
                        tdClass: 'text-right',
                        thClass: 'text-right',
                        thStyle: { width: '120px' }
                    }
                ],
                totalRows: 0,
                currentPage: 1,
                perPage: 5,
                sortBy: '',
                sortDesc: false,
                sortDirection: 'asc',
                filter: null,
                isLoading: false
            };
        },
        watch: {
            positions: {
                immediate: true,
                handler(newVal) {
                    this.totalRows = newVal ? newVal.length : 0;
                }
            }
        },
        methods: {
            open() {
                this.visible = true;
                this.selectedIds = [];
                this.filter = null;
                this.currentPage = 1;
            },
            closeModal() {
                this.visible = false;
                this.selectedIds = [];
            },
            onFiltered(filteredItems) {
                this.totalRows = filteredItems.length;
                if (this.currentPage > Math.ceil(this.totalRows / this.perPage)) {
                    this.currentPage = 1;
                }
            },
            getUrl(id) {
                return getDownloadUrlByArticleId(id);
            },
            onLackMailClosed() {
                this.closeModal();
                this.$emit('sent');
            }
        }
    };
</script>
